import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Table as TableIcon } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Card, Col, Container, Row } from 'reactstrap';
import { Breadcrumbs } from '../../AbstractElements';
import { useLoader } from '../../_helper/LoaderContext';
import { addHeaderFooterValue } from './service/setting-service';
import { toast } from 'react-toastify';
import { useLab } from '../../_helper/PharmacyProvider';
import S3ImageLoader from '../../S3ImageLoader';
import { getFileUrl } from '../../utils/getFileUrl';

import Quill from 'quill';
import TableUp, {
    defaultCustomSelect,
    TableSelection,
    TableResizeLine,
    TableResizeScale,
    TableAlign,
    TableMenuContextmenu,
    TableClipboard,
} from 'quill-table-up';
import 'quill-table-up/index.css';
import 'quill-table-up/table-creator.css';

// Safe helper to find an ancestor blot without throwing when the target is not in a table
const findParentBlotSafe = (blot, targetBlotName) => {
    if (!blot) return null;
    try {
        let target = blot.parent;
        while (target && target.statics?.blotName !== targetBlotName && target !== blot.scroll) {
            target = target.parent;
        }
        if (!target || target === blot.scroll) {
            return null;
        }
        return target;
    } catch {
        return null;
    }
};

// Ensure ReactQuill creates editor instances using the Quill 2 constructor and handles Quill 2 clipboard API
ReactQuill.Quill = Quill;
if (!ReactQuill.prototype._quill2Patched) {
    ReactQuill.prototype.createEditor = function (element, config) {
        const editor = new Quill(element, config);
        if (editor.clipboard) {
            const origConvert = editor.clipboard.convert.bind(editor.clipboard);
            editor.clipboard.convert = function (content, formats) {
                if (typeof content === 'string') {
                    return origConvert({ html: content, text: '' }, formats);
                }
                return origConvert(content, formats);
            };
        }
        if (config.tabIndex != null) {
            this.setEditorTabIndex(editor, config.tabIndex);
        }
        this.hookEditor(editor);
        return editor;
    };
    ReactQuill.prototype._quill2Patched = true;
}

// ReactQuill calls editor.clipboard.convert(value) passing a raw string (Quill 1 API).
// In Quill 2, convert expects { html: string, text?: string }.
// Patch setEditorContents so ReactQuill properly converts HTML string to Delta when loading or updating text.
ReactQuill.prototype.setEditorContents = function (editor, value) {
    this.value = value;
    const sel = this.getEditorSelection ? this.getEditorSelection() : null;
    if (typeof value === 'string') {
        if (editor && editor.clipboard) {
            const delta = editor.clipboard.convert({ html: value, text: '' });
            editor.setContents(delta);
        } else if (editor && editor.root) {
            editor.root.innerHTML = value;
        }
    } else if (value && editor) {
        editor.setContents(value);
    }
    const _this = this;
    Promise.resolve().then(() => {
        if (_this.setEditorSelection && sel) {
            _this.setEditorSelection(editor, sel);
        }
    });
};

const Clipboard = Quill.import('modules/clipboard');
if (Clipboard && Clipboard.prototype && !Clipboard.prototype._stringConvertPatched) {
    const origConvert = Clipboard.prototype.convert;
    Clipboard.prototype.convert = function (content, formats) {
        if (typeof content === 'string') {
            return origConvert.call(this, { html: content, text: '' }, formats);
        }
        return origConvert.call(this, content, formats);
    };
    Clipboard.prototype._stringConvertPatched = true;
}

if (TableClipboard && TableClipboard.prototype && !TableClipboard.prototype._stringConvertPatched) {
    const origTableConvert = TableClipboard.prototype.convert;
    TableClipboard.prototype.convert = function (content, formats) {
        if (typeof content === 'string') {
            return origTableConvert.call(this, { html: content, text: '' }, formats);
        }
        return origTableConvert.call(this, content, formats);
    };
    TableClipboard.prototype._stringConvertPatched = true;
}

// Register quill-table-up module with Quill 2 avoiding duplicate registration
if (!Quill.imports['modules/table-up']) {
    Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);
}

// Fallback safety: ensure insertTable has selection even if editor not focused
if (!TableUp.prototype._safeInsertPatched) {
    const origInsertTable = TableUp.prototype.insertTable;
    TableUp.prototype.insertTable = function (rows, columns, source) {
        this.quill.focus();
        let range = this.quill.getSelection();
        if (!range) {
            const length = this.quill.getLength();
            const index = Math.max(0, length - 1);
            this.quill.setSelection(index, 0, Quill.sources.SILENT);
        }
        return origInsertTable.call(this, rows, columns, source);
    };
    TableUp.prototype._safeInsertPatched = true;
}

// Register table icon for toolbar
const icons = Quill.import('ui/icons');
if (icons) {
    if (!icons['table']) {
        icons['table'] = '<svg viewBox="0 0 18 18"><rect class="ql-stroke" height="12" width="14" x="2" y="3"></rect><line class="ql-stroke" x1="2" x2="16" y1="7" y2="7"></line><line class="ql-stroke" x1="2" x2="16" y1="11" y2="11"></line><line class="ql-stroke" x1="7" x2="7" y1="3" y2="15"></line><line class="ql-stroke" x1="11" x2="11" y1="3" y2="15"></line></svg>';
    }
    if (!icons['table-up']) {
        icons['table-up'] = icons['table'];
    }
}

export default function HeaderHome() {
    const { t } = useTranslation();
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [isLogoRemoved, setIsLogoRemoved] = useState(false);
    const { setLoading } = useLoader();
    const [headerText, setHeaderText] = useState(``);
    const fileInputRef = useRef(null);
    const quillRef = useRef(null);
    const lastRangeRef = useRef(null);
    const activeCellRef = useRef(null);
    const [isInsideTable, setIsInsideTable] = useState(false);

    const getSelectedTds = useCallback((quill) => {
        if (!quill) return [];
        const tableUp = quill.getModule(TableUp.moduleName);
        const tableSelection = tableUp?.getModule(TableSelection.moduleName);
        if (tableSelection?.selectedTds && tableSelection.selectedTds.length > 0) {
            return tableSelection.selectedTds;
        }

        const range = quill.getSelection();
        if (range) {
            const [line] = quill.getLine(range.index);
            const cellInner = line && findParentBlotSafe(line, 'table-up-cell-inner');
            if (cellInner) return [cellInner];
        }

        if (activeCellRef.current) {
            const blot = Quill.find(activeCellRef.current);
            const cellInner = blot?.getCellInner ? blot.getCellInner() : (blot?.statics?.blotName === 'table-up-cell-inner' ? blot : findParentBlotSafe(blot, 'table-up-cell-inner'));
            if (cellInner) return [cellInner];
        }

        return [];
    }, []);

    const handleTableAction = useCallback((action) => {
        const quill = quillRef.current?.getEditor?.();
        if (!quill) return;
        const tableUp = quill.getModule(TableUp.moduleName);
        if (!tableUp) return;

        const selectedTds = getSelectedTds(quill);
        if (!selectedTds || selectedTds.length === 0) return;

        switch (action) {
            case 'insertRowAbove':
                tableUp.appendRow(selectedTds, false);
                break;
            case 'insertRowBelow':
                tableUp.appendRow(selectedTds, true);
                break;
            case 'insertColumnLeft':
                tableUp.appendCol(selectedTds, false);
                break;
            case 'insertColumnRight':
                tableUp.appendCol(selectedTds, true);
                break;
            case 'mergeCells':
                tableUp.mergeCells(selectedTds);
                break;
            case 'splitCell':
                tableUp.splitCell(selectedTds);
                break;
            case 'deleteRow':
                tableUp.removeRow(selectedTds);
                break;
            case 'deleteColumn':
                tableUp.removeCol(selectedTds);
                break;
            case 'deleteTable':
                tableUp.deleteTable(selectedTds);
                setIsInsideTable(false);
                activeCellRef.current = null;
                break;
            default:
                break;
        }

        setHeaderText(quill.root.innerHTML);
    }, [getSelectedTds]);

    const handleSelectionChange = useCallback((range) => {
        if (range) {
            lastRangeRef.current = range;
        }
        const quill = quillRef.current?.getEditor?.();
        if (quill && range) {
            try {
                const [line] = quill.getLine(range.index);
                const cellInner = line && findParentBlotSafe(line, 'table-up-cell-inner');
                if (cellInner) {
                    setIsInsideTable(true);
                    activeCellRef.current = cellInner.domNode;
                    return;
                }
            } catch {
                // Ignore any blot traversal errors
            }
        }
        const tableUp = quill?.getModule(TableUp.moduleName);
        const tableSelection = tableUp?.getModule(TableSelection.moduleName);
        if (tableSelection?.selectedTds && tableSelection.selectedTds.length > 0) {
            setIsInsideTable(true);
            return;
        }
        setIsInsideTable(false);
    }, []);

    const modules = useMemo(() => ({
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['blockquote', 'link'],
            [{ [TableUp.toolName]: [] }],
            ['clean']
        ],
        [TableUp.moduleName]: {
            customSelect: defaultCustomSelect,
            customBtn: true,
            modules: [
                { module: TableSelection },
                { module: TableResizeLine },
                { module: TableResizeScale },
                { module: TableAlign },
                { module: TableMenuContextmenu }
            ]
        }
    }), []);

    useEffect(() => {
        const loadHeaderData = async () => {
            try {
                const header = JSON.parse(localStorage.getItem("header"));
                if (header) {
                    if (header.headerLogo && header.headerLogo !== "") {
                        const url = await getFileUrl(header.headerLogo);
                        setLogoPreview(url || header.headerLogo);
                    }
                    if (header.headerTest) {
                        setHeaderText(header.headerTest);
                        const quill = quillRef.current?.getEditor?.();
                        if (quill && quill.clipboard) {
                            const delta = quill.clipboard.convert({ html: header.headerTest, text: '' });
                            quill.setContents(delta, Quill.sources.SILENT);
                        }
                    }
                }
            } catch (err) {
                console.error("Error loading header settings:", err);
            }
        };
        loadHeaderData();
    }, []);

    useEffect(() => {
        const quill = quillRef.current?.getEditor?.();
        const editorRoot = quill?.root;
        if (!editorRoot) return;

        const checkTableContext = (e) => {
            const target = e?.target;
            const cell = target?.closest ? target.closest('td, th, .ql-table-cell, .ql-table-cell-inner') : null;
            if (cell && editorRoot.contains(cell)) {
                activeCellRef.current = cell;
                setIsInsideTable(true);

                if (e.type === 'contextmenu') {
                    const tableUp = quill.getModule(TableUp.moduleName);
                    const tableSelection = tableUp?.getModule(TableSelection.moduleName);
                    if (tableSelection && (!tableSelection.selectedTds || tableSelection.selectedTds.length === 0)) {
                        const cellBlot = Quill.find(cell);
                        const innerBlot = cellBlot?.getCellInner ? cellBlot.getCellInner() : (cellBlot?.statics?.blotName === 'table-up-cell-inner' ? cellBlot : findParentBlotSafe(cellBlot, 'table-up-cell-inner'));
                        if (innerBlot) {
                            tableSelection.setSelectionTable(cell.closest('table'));
                            tableSelection.setSelectedTds([innerBlot]);
                            tableSelection.updateWithSelectedTds();
                        }
                    }
                }
            } else if (e && e.type === 'click') {
                const inTable = target?.closest ? target.closest('table, .ql-table, .table-up-menu') : null;
                const inToolbarOrMenu = target?.closest ? (target.closest('.ql-toolbar') || target.closest('button')) : null;
                if (!inTable && !inToolbarOrMenu) {
                    setIsInsideTable(false);
                    activeCellRef.current = null;
                }
            }
        };

        editorRoot.addEventListener('click', checkTableContext);
        editorRoot.addEventListener('keyup', checkTableContext);
        editorRoot.addEventListener('contextmenu', checkTableContext, true);

        return () => {
            editorRoot.removeEventListener('click', checkTableContext);
            editorRoot.removeEventListener('keyup', checkTableContext);
            editorRoot.removeEventListener('contextmenu', checkTableContext, true);
        };
    }, []);

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Check file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.warning(t("Image must be below 2MB"));
            e.target.value = ''; // Clear the input
            return;
        }

        // Check file type - only allow JPG, JPEG, PNG
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            toast.warning(t("Only JPG, JPEG, and PNG images are allowed"));
            e.target.value = ''; // Clear the input
            return;
        }

        setLoading(true);
        try {
            const form_data = new FormData();
            form_data.append("headerText", headerText || "");
            form_data.append("logoImg", file, file.name);

            const res = await addHeaderFooterValue(form_data);
            if (res?.data?.success === true) {
                const uploadedKey = res?.data?.data?.headerLogo;
                if (!uploadedKey) {
                    toast.error(t("Upload succeeded, but no logo filename was returned"));
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }

                const presignedUrl = await getFileUrl(uploadedKey);
                if (!presignedUrl) {
                    toast.error(t("Failed to retrieve presigned URL for logo"));
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    return;
                }

                setLogo(file);
                setLogoPreview(presignedUrl);
                setIsLogoRemoved(false);

                const existingHeader = JSON.parse(localStorage.getItem("header")) || {};
                const headerFooter = {
                    ...existingHeader,
                    headerTest: res.data.data.headerText !== undefined ? res.data.data.headerText : headerText,
                    footerText: res.data.data.footerText !== undefined ? res.data.data.footerText : existingHeader.footerText,
                    headerLogo: uploadedKey,
                };
                localStorage.setItem("header", JSON.stringify(headerFooter));

                toast.success(res.data.message || t("Logo uploaded successfully"));
            } else {
                toast.error(res?.data?.message || t("Failed to upload logo"));
                if (fileInputRef.current) fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error("Logo upload error:", error);
            toast.error(error?.response?.data?.message || t("Failed to upload logo"));
            if (fileInputRef.current) fileInputRef.current.value = '';
        } finally {
            setLoading(false);
        }
    };

    const removeLogo = () => {
        setLogo(null);
        setLogoPreview(null);
        setIsLogoRemoved(true);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handelAdd = async () => {
        setLoading(true)
        try {
            const form_data = new FormData();
            form_data.append("headerText", headerText);
            if (logo) {
                form_data.append("logoImg", logo, logo.name);
            } else if (isLogoRemoved) {
                form_data.append("logoImg", null);
                form_data.logoImg = null;
            }
            // form_data.forEach((key,value) => console.log(key+" :" +value))
            const res = await addHeaderFooterValue(form_data)
            if (res.data.success == true) {
                toast.success(res.data.message);
                setIsLogoRemoved(false);
                // setHeaderText("");
                // setLogo(null);
                const headerFooter = {
                    headerTest: res.data.data.headerText,
                    footerText: res.data.data.footerText,
                    headerLogo: res.data.data.headerLogo
                }
                localStorage.setItem("header", JSON.stringify(headerFooter))
                // setLogoPreview(null)
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <Breadcrumbs mainTitle={t("Header Setting")} parent={t("Home")} title={t("Header Setting")} />
            <Container fluid={true}>
                <Row>
                    <Col className="h-100">
                        <Card className="tw-p-8">
                            <div className="">
                                {/* COMPACT REALISTIC HEADER PREVIEW - Like on actual report */}
                                <div className="">
                                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-6">
                                        {/* Small Logo */}
                                        <div className="tw-flex-shrink-0">
                                            {logoPreview ? (
                                                <S3ImageLoader
                                                    fileName={logoPreview}
                                                    fallbackSrc={''}
                                                    className="tw-h-[150px] tw-w-[150px] tw-object-contain tw-max-w-48"
                                                    alt="logoPreview" />
                                            ) : (
                                                <div className="tw-h-16 tw-w-32 tw-bg-gray-200 tw-border-2 tw-border-dashed tw-rounded tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-xs">
                                                    {t("No Logo")}
                                                </div>
                                            )}
                                        </div>

                                        {/* Compact Text - Right Aligned */}
                                        <div className="tw-flex-1 tw-text-right">
                                            <div
                                                className="
                                    tw-w-full
                                    [&>*]:tw-block [&>*]:tw-my-0 [&>*]:tw-leading-tight

                                    /* Table styles */
                                    [&_table]:tw-table [&_table]:tw-border-collapse [&_table]:tw-w-full [&_table]:tw-my-2 [&_table]:tw-border [&_table]:tw-border-gray-400
                                    [&_td]:tw-border [&_td]:tw-border-gray-400 [&_td]:tw-p-1.5 [&_td]:tw-text-xs [&_td]:tw-align-top
                                    [&_th]:tw-border [&_th]:tw-border-gray-400 [&_th]:tw-p-1.5 [&_th]:tw-text-xs [&_th]:tw-bg-gray-100 [&_th]:tw-font-semibold

                                    /* Blockquote styles */
                                    [&_blockquote]:tw-border-l-4 [&_blockquote]:tw-border-gray-300 [&_blockquote]:tw-pl-3 [&_blockquote]:tw-italic [&_blockquote]:tw-my-1

                                    /* Link styles */
                                    [&_a]:tw-text-blue-600 [&_a]:tw-underline

                                    /* Inline alignment */
                                    [&_[style*='text-align:left']]:tw-text-left
                                    [&_[style*='text-align:center']]:tw-text-center
                                    [&_[style*='text-align:right']]:tw-text-right
                                    [&_[style*='text-align:justify']]:tw-text-justify

                                    /* Quill alignment classes */
                                    [&_.ql-align-left]:tw-text-left
                                    [&_.ql-align-center]:tw-text-center
                                    [&_.ql-align-right]:tw-text-right
                                    [&_.ql-align-justify]:tw-text-justify
                                "
                                                dangerouslySetInnerHTML={{ __html: headerText }}
                                            />
                                        </div>
                                    </div>

                                    <hr className="tw-mt-4 tw-border-t-2 tw-border-green-700" />
                                    <p className="tw-text-center tw-text-xs tw-text-gray-500 tw-mt-2">{t("Header Preview (as it will appear on reports)")}</p>
                                </div>

                                {/* EDIT PANELS */}
                                <div className="row">
                                    {/* LOGO UPLOAD */}
                                    <div className="col-md-3">

                                        <h3 className="tw-text-lg tw-font-semibold tw-mb-4 tw-text-gray-700">
                                            {t("Upload Lab Logo")}
                                        </h3>

                                        {/* Upload Box */}
                                        <div
                                            onClick={(e) => {
                                                if (e.target.closest('button') || e.target.closest('label')) return;
                                                fileInputRef.current?.click();
                                            }}
                                            className="tw-border tw-border-gray-300 tw-rounded-xl tw-bg-white tw-p-6 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-cursor-pointer hover:tw-shadow-md tw-transition-all tw-min-h-[220px]"
                                        >
                                            {/* Preview OR Icon */}
                                            {logoPreview ? (
                                                <S3ImageLoader
                                                    fileName={logoPreview}
                                                    fallbackSrc={''}
                                                    // className="m-0 rounded-circle border border-5 border-white shadow-lg"
                                                    alt="logoPreview"
                                                    style={{ width: "100%", height: "100px", objectFit: "contain" }} />
                                            ) : (
                                                <div
                                                    className="tw-flex tw-flex-col tw-items-center tw-text-center tw-text-gray-500 tw-cursor-pointer"
                                                    onClick={() => fileInputRef.current?.click()}
                                                >
                                                    <Upload className="tw-w-10 tw-h-10 tw-text-gray-400" />
                                                    <p className="tw-mt-2 tw-text-sm">{t("Click to upload logo")}</p>
                                                    <p className="tw-text-[10px] tw-text-gray-400">{t("PNG / JPG • Max 2MB")}</p>
                                                </div>
                                            )}

                                            {/* File Input */}
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/png, image/jpg, image/jpeg"
                                                onChange={handleLogoUpload}
                                                className="tw-hidden"
                                                id="logo-uploader"
                                            />

                                            {/* CTA Button */}
                                            <label
                                                htmlFor="logo-uploader"
                                                className="tw-px-6 tw-py-2 tw-bg-green-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-medium hover:tw-bg-green-700 tw-transition"
                                            >
                                                {logoPreview ? t("Change Logo") : t("Choose Logo")}
                                            </label>

                                            {/* Remove Button */}
                                            {logoPreview && (
                                                <button
                                                    type="button"
                                                    onClick={removeLogo}
                                                    className="btn btn-secondary"
                                                >
                                                    {t("Remove Logo")}
                                                </button>
                                            )}
                                        </div>

                                    </div>

                                    <div className="col-md-9">
                                        {/* RICH TEXT EDITOR */}
                                        <div>
                                            <h3 className="tw-text-xl tw-font-semibold tw-mb-4">{t("Lab Name & Address (Right Side)")}</h3>

                                            <div className="
                                                tw-border tw-border-gray-300 tw-rounded-lg tw-overflow-visible tw-shadow-sm tw-bg-white
                                                [&_.ql-toolbar]:tw-border-none [&_.ql-toolbar]:tw-border-b [&_.ql-toolbar]:tw-border-gray-200 [&_.ql-toolbar]:tw-bg-gray-50/70
                                                [&_.ql-container]:tw-border-none
                                                [&_.ql-picker.ql-table-up_.ql-picker-label]:tw-flex [&_.ql-picker.ql-table-up_.ql-picker-label]:tw-items-center [&_.ql-picker.ql-table-up_.ql-picker-label]:tw-justify-center
                                                [&_.ql-picker.ql-table-up_.ql-picker-options]:tw-z-50 [&_.ql-picker.ql-table-up_.ql-picker-options]:tw-shadow-lg [&_.ql-picker.ql-table-up_.ql-picker-options]:tw-rounded-md [&_.ql-picker.ql-table-up_.ql-picker-options]:tw-border [&_.ql-picker.ql-table-up_.ql-picker-options]:tw-border-gray-200
                                                [&_.ql-editor_blockquote]:tw-border-l-4 [&_.ql-editor_blockquote]:tw-border-gray-300 [&_.ql-editor_blockquote]:tw-pl-3 [&_.ql-editor_blockquote]:tw-italic [&_.ql-editor_blockquote]:tw-my-1
                                                [&_.ql-editor_a]:tw-text-blue-600 [&_.ql-editor_a]:tw-underline
                                            ">
                                                {/* CONTEXTUAL TABLE BAR (ACTIVE WHEN CURSOR IS IN A TABLE) */}
                                                {isInsideTable && (
                                                    <div className="tw-bg-emerald-50/80 tw-border-b tw-border-emerald-100 tw-px-3 tw-py-1.5 tw-flex tw-items-center tw-flex-wrap tw-gap-1.5 tw-text-xs">
                                                        <span className="tw-font-medium tw-text-emerald-900 tw-flex tw-items-center tw-gap-1 tw-mr-1">
                                                            <TableIcon className="tw-w-3.5 tw-h-3.5 tw-text-emerald-700" />
                                                            {t("Table")}:
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('insertRowAbove')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-emerald-100 tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-transition"
                                                            title={t("Insert row above current cell")}
                                                        >
                                                            {t("+ Row Above")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('insertRowBelow')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-emerald-100 tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-transition"
                                                            title={t("Insert row below current cell")}
                                                        >
                                                            {t("+ Row Below")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('insertColumnLeft')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-emerald-100 tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-transition"
                                                            title={t("Insert column to the left")}
                                                        >
                                                            {t("+ Col Left")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('insertColumnRight')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-emerald-100 tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-transition"
                                                            title={t("Insert column to the right")}
                                                        >
                                                            {t("+ Col Right")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('mergeCells')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-emerald-100 tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-transition"
                                                            title={t("Merge selected cells")}
                                                        >
                                                            {t("Merge")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('splitCell')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-emerald-100 tw-text-gray-700 tw-border tw-border-gray-300 tw-rounded tw-text-xs tw-transition"
                                                            title={t("Split cell")}
                                                        >
                                                            {t("Split")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('deleteRow')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-amber-100 tw-text-amber-800 tw-border tw-border-amber-300 tw-rounded tw-text-xs tw-transition"
                                                            title={t("Delete current row")}
                                                        >
                                                            {t("Delete Row")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('deleteColumn')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-amber-100 tw-text-amber-800 tw-border tw-border-amber-300 tw-rounded tw-text-xs tw-transition"
                                                            title={t("Delete current column")}
                                                        >
                                                            {t("Delete Col")}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => handleTableAction('deleteTable')}
                                                            className="tw-px-2 tw-py-1 tw-bg-white hover:tw-bg-red-100 tw-text-red-700 tw-border tw-border-red-300 tw-rounded tw-text-xs tw-transition tw-ml-auto"
                                                            title={t("Delete entire table")}
                                                        >
                                                            {t("Delete Table")}
                                                        </button>
                                                    </div>
                                                )}

                                                <ReactQuill
                                                    ref={quillRef}
                                                    theme="snow"
                                                    value={headerText}
                                                    onChange={setHeaderText}
                                                    onChangeSelection={handleSelectionChange}
                                                    placeholder={`ClinicDots Diagnostic Center\n123, MG Road, Near City Hospital\nMumbai - 400001, Maharashtra\nPhone: +91 98765 43210 | Email: info@example.com\nNABL Accredited | ISO 9001:2015 Certified`}
                                                    className="header-quill-editor tw-flex tw-flex-col [&_.ql-container]:!tw-h-auto [&_.ql-editor]:tw-min-h-[200px]"
                                                    modules={modules}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* SAVE BUTTON */}
                                <div className="tw-text-right tw-mt-6">
                                    <button className="btn btn-primary" onClick={handelAdd}>
                                        {t("Save Header Design")}
                                    </button>
                                </div>
                            </div>
                        </Card >
                    </Col>
                </Row>
            </Container>
        </>
    );
}